import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { isEqual } from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker, useNavigate } from "react-router";

import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useUpdatePassword, { UpdatePasswordInput } from "../../api/useUpdatePassword";

type Props = {
    token: string;
    submitButtonText?: string;
};

export default function UpdatePasswordForm({ token, submitButtonText }: Props) {
    const { t } = useTranslation("auth");
    const { updatePassword, error } = useUpdatePassword();
    const {
        handleSubmit,
        register,
        setError,
        watch,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
    } = useForm<UpdatePasswordInput & { confirmNewPassword?: string }>({
        defaultValues: {
            token,
            newPassword: undefined,
            confirmNewPassword: undefined,
        },
    });
    const navigate = useNavigate();

    const onSubmit: SubmitHandler<UpdatePasswordInput> = async (data: UpdatePasswordInput) => {
        await updatePassword({
            token: data.token,
            newPassword: data.newPassword,
        });
        navigate("/login");
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    const newPassword = watch("newPassword");
    const confirmNewPassword = watch("confirmNewPassword");

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    label={t("resetPasswordForm.labelNewPassword")}
                    error={!!errors.newPassword}
                    helperText={t("resetPasswordForm.helperTextNewPassword")}
                    {...register("newPassword", {
                        required: t("resetPasswordForm.labelNewPasswordRequired"),
                    })}
                    type="password"
                />

                <TextField
                    label={t("resetPasswordForm.labelConfirmNewPassword")}
                    error={!!errors.confirmNewPassword}
                    {...register("confirmNewPassword", {
                        required: t("resetPasswordForm.labelConfirmNewPasswordRequired"),
                    })}
                    type="password"
                />

                <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty || !newPassword || !isEqual(newPassword, confirmNewPassword)}
                    loading={isLoading}
                >
                    {submitButtonText ?? t("resetPasswordForm.resetPassword")}
                </Button>
            </Stack>
        </form>
    );
}
